import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaClock, FaDumbbell, FaCheck, FaTimes, FaCalendarAlt } from 'react-icons/fa';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import { gymKgToDisplay } from '../../utils/weightUnits';
import './SessionDetailPage.css';

const SessionDetailPage = () => {
  const weightUnit = useWeightUnit();
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/fitness/sessions/${sessionId}`,
        {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        }
      );
      if (!response.ok) throw new Error('Failed to fetch session details');
      const data = await response.json();
      setSession(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown date';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="session-detail-page">
        <div className="session-loading">
          <div className="loading-spinner" />
          <p>Loading session details...</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="session-detail-page">
        <div className="session-error">
          <p>Error: {error || 'Session not found'}</p>
          <button onClick={() => navigate('/fitness/history')}>Back to History</button>
        </div>
      </div>
    );
  }

  // Use exerciseLogs if available, otherwise fall back to exercises array
  const exerciseData = session.exerciseLogs?.length > 0 ? session.exerciseLogs : session.exercises || [];
  const completedExercises = exerciseData.filter(e => e.completed) || [];
  const skippedExercises = exerciseData.filter(e => e.skipped) || [];

  return (
    <div className="session-detail-page">
      <div className="session-header">
        <button className="back-btn" onClick={() => navigate('/fitness/history')}>
          <FaArrowLeft />
          <span>Back to History</span>
        </button>

        <div className="session-header-content">
          <span className={`session-status-badge ${session.status}`}>
            {session.status === 'completed' ? 'Completed' : session.status === 'in_progress' ? 'In progress' : session.status === 'abandoned' ? 'Abandoned' : session.status}
          </span>
          <h1>{session.workoutName || 'Workout Session'}</h1>
          
          <div className="session-date">
            <FaCalendarAlt />
            <span>{formatDate(session.startedAt)}</span>
            {session.startedAt && (
              <span className="session-time">at {formatTime(session.startedAt)}</span>
            )}
          </div>
        </div>
      </div>

      <div className="session-container">
        {/* Stats Summary */}
        <div className="session-stats">
          <div className="stat-card">
            <FaClock className="stat-icon" />
            <div className="stat-info">
              <span className="stat-value">{session.actualDuration || '?'}</span>
              <span className="stat-label">Minutes</span>
            </div>
          </div>
          
          <div className="stat-card">
            <FaCheck className="stat-icon completed" />
            <div className="stat-info">
              <span className="stat-value">{completedExercises.length}</span>
              <span className="stat-label">Completed</span>
            </div>
          </div>
          
          <div className="stat-card">
            <FaTimes className="stat-icon skipped" />
            <div className="stat-info">
              <span className="stat-value">{skippedExercises.length}</span>
              <span className="stat-label">Skipped</span>
            </div>
          </div>
          
          <div className="stat-card">
            <FaDumbbell className="stat-icon" />
            <div className="stat-info">
              <span className="stat-value">{exerciseData.length}</span>
              <span className="stat-label">Total Exercises</span>
            </div>
          </div>
        </div>

        {/* Exercise Log */}
        <div className="exercise-log-section">
          <h2>Exercise Log</h2>
          
          <div className="exercise-log-list">
            {exerciseData.map((exercise, idx) => (
              <div 
                key={idx} 
                className={`exercise-log-item ${exercise.completed ? 'completed' : ''} ${exercise.skipped ? 'skipped' : ''}`}
              >
                <div className="exercise-log-header">
                  <span className="exercise-number">{idx + 1}</span>
                  <h4>{exercise.exerciseName}</h4>
                  <span className={`exercise-status ${exercise.completed ? 'completed' : exercise.skipped ? 'skipped' : 'pending'}`}>
                    {exercise.completed ? <FaCheck /> : exercise.skipped ? 'Skipped' : 'Pending'}
                  </span>
                </div>

                {exercise.sets && exercise.sets.length > 0 && (
                  <div className="exercise-sets">
                    {exercise.sets.map((set, setIdx) => (
                      <div key={setIdx} className={`set-row ${set.completed ? 'completed' : ''}`}>
                        <span className="set-number">Set {set.setNumber}</span>
                        {set.reps && <span className="set-reps">{set.reps} reps</span>}
                        {set.weight && <span className="set-weight">{gymKgToDisplay(set.weight, weightUnit).value} {weightUnit}</span>}
                        {set.duration && <span className="set-duration">{Math.floor(set.duration / 60)}:{(set.duration % 60).toString().padStart(2, '0')}</span>}
                        {set.completed && <FaCheck className="set-check" />}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Session Notes */}
        {session.notes && (
          <div className="session-notes">
            <h3>Notes</h3>
            <p>{session.notes}</p>
          </div>
        )}

        {/* Rating & Feedback */}
        {(session.rating || session.perceivedDifficulty || session.feelingAfter) && (
          <div className="session-feedback">
            <h3>Session Feedback</h3>
            <div className="feedback-grid">
              {session.rating && (
                <div className="feedback-item">
                  <span className="feedback-label">Rating</span>
                  <span className="feedback-value">{'⭐'.repeat(session.rating)}</span>
                </div>
              )}
              {session.perceivedDifficulty && (
                <div className="feedback-item">
                  <span className="feedback-label">Difficulty</span>
                  <span className="feedback-value">{session.perceivedDifficulty.replace('_', ' ')}</span>
                </div>
              )}
              {session.feelingAfter && (
                <div className="feedback-item">
                  <span className="feedback-label">Feeling After</span>
                  <span className="feedback-value">{session.feelingAfter}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionDetailPage;

